import { DatePicker } from "@rumbledirect/ui-v2";
import { isDay, toISODate } from "@rumbledirect/ui-v2/day";
import type { DatePickerQuestionModel } from "./models";
import { useSurveyQuestionRerender } from "./useSurveyQuestionRerender";

/**
 * Renderer for the schema's `datepicker` question type over admin-v2's
 * `DatePicker`.
 *
 * The model value is the bare `YYYY-MM-DD` day the control speaks — a damage
 * *date*, not an instant, and therefore timezone-stable (an earlier version
 * stored a UTC instant, which `toDay()` reinterprets in the viewer's
 * timezone: the picked date could shift after a timezone move). Converting
 * to the offset-bearing local-midnight timestamp the create-dossier DTO
 * (`RepairDirectGenericDossierIn::$damageAt`, `date-time`) declares is the
 * submission mapper's job — the same shape the direct-create form's
 * `toDamageAtTimestamp()` produces, so both routes into `damageAt` agree.
 * That mapper arrives with the `/dossiers/new` page (PR 4); until then the
 * value is validated against `DAY_PATTERN` before the control trusts it.
 *
 * Chrome (label, description, errors) lives in `SurveyQuestionChrome`;
 * this is only the control.
 */
export function SurveyDatePickerQuestion({
  question,
}: {
  readonly question: DatePickerQuestionModel;
}) {
  useSurveyQuestionRerender(question);
  const value = question.value;
  // Recomputed every render, not memoized: the page can sit open across
  // local midnight, and a stale value would keep blocking the newly current
  // day. Same reasoning as `DossierCreateSheet.tsx`'s `todayISODate`.
  const todayISODate = toISODate(new Date());

  return (
    <DatePicker
      id={question.inputId}
      // The chrome labels the trigger via `aria-labelledby` (its `htmlFor`
      // points at `inputId`); the label falls back to the question title for
      // the chromeless context this renderer is also unit-tested in.
      aria-label={
        question.a11y_input_ariaLabel ?? question.title ?? question.name
      }
      // SurveyJS exposes a11y flags as "true"/"false" strings; the
      // DatePicker primitive takes booleans.
      aria-invalid={question.a11y_input_ariaInvalid === "true"}
      aria-describedby={question.a11y_input_ariaDescribedBy ?? undefined}
      value={typeof value === "string" && isDay(value) ? value : ""}
      onValueChange={(next) => {
        question.value = next === "" ? undefined : next;
      }}
      placeholder={question.renderedPlaceholder ?? undefined}
      max={todayISODate}
      // The trigger is a button, not a native input, so there is no
      // `readonly` behaviour to fall back on — read-only disables it, same
      // as the dropdown renderer.
      disabled={question.isDisabledAttr || question.isReadOnlyAttr}
    />
  );
}
